import React, { useCallback, useMemo } from 'react';
import {
	GeofenceStatus,
	PointMetaData,
	PolygonValidationStatus,
} from 'interfaces/awd.interface';
import { useAppSelector } from 'store/hooks';
// import ClearIcon from '@mui/icons-material/Clear';
// import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

import DrawingMode from 'sharedMapComponents/DrawingMode';
import PolygonWithMarkers from 'sharedMapComponents/PolygonWithMarkers';
import { trackInvalidGeofenceDrawn } from 'thirdParty/firebaseAnalytics';
import { useSearchParams } from 'customHooks/useSearchParams';
import { getUgdnFromLocalStorage } from 'utils/helpers';
import { EditablePolygon } from 'interfaces/maps.interface';

type DrawingContainerProps = Record<string, never>;

// TODO: separate drawing containers for separate drawing behaviour
const DrawingContainer: React.FC<DrawingContainerProps> = () => {
	const searchParams = useSearchParams();

	const selectedPoint = useAppSelector((s) => s.awdView.selectedPoint);
	const isDrawEnabled = useAppSelector((s) => s.awdView.isDrawEnabled);
	const editablePolygons = useAppSelector((s) => s.awdView.editablePolygons);
	// const polygonMapTiles = useAppSelector((s) => s.awdView.polygonMapTiles);
	const pointsOfFarmId = useAppSelector((s) => s.awdView.pointsOfFarmId);

	const farmId = searchParams.get('farmId');
	const editablePolygonsList = useMemo(
		() => Object.values(editablePolygons),
		[editablePolygons],
	);
	// const neighbouringPolygonsList = useMemo(
	// 	() => Object.values(polygonMapTiles),
	// 	[polygonMapTiles],
	// );

	// const getVerticesOfNeighbouringPolygons = useCallback(
	// 	(neighbouringPolygonsList) => {
	// 		return neighbouringPolygonsList.reduce(
	// 			(acc: google.maps.LatLng[], cur: any) => {
	// 				acc = [...acc, ...cur.geometry];
	// 				return acc;
	// 			},
	// 			[],
	// 		);
	// 	},
	// 	[],
	// );

	const getVerticesOfEditablePolygons = useCallback(
		(editablePolygonsList, polygonIndex) => {
			return editablePolygonsList.reduce(
				(acc: google.maps.LatLng[], cur: any, idx: number) => {
					if (polygonIndex !== undefined && idx === polygonIndex)
						return acc;
					acc = [...acc, ...cur.geometry];
					return acc;
				},
				[],
			);
		},
		[],
	);

	const getOverlappingPointsOfNeighbouringPolygonsCount = useCallback(
		(allVertices, polygon) => {
			let totalPoints = 0;
			allVertices.forEach((el: google.maps.LatLng) => {
				if (google.maps.geometry.poly.containsLocation(el, polygon)) {
					totalPoints += 1;
				}
			});
			return totalPoints;
		},
		[],
	);

	const getOverlappingPointsOfCurrentPolygonCount = useCallback(
		(editablePolygonsList, polygon, polygonIndex) => {
			let totalPoints = 0;
			const polygonPath = polygon
				.getPath()
				.getArray()
				.map((coord: google.maps.LatLng) => {
					return {
						lat: coord.lat(),
						lng: coord.lng(),
					};
				});
			editablePolygonsList.forEach(
				(polygon: EditablePolygon, idx: number) => {
					if (idx !== polygonIndex) {
						polygonPath.forEach((point: google.maps.LatLng) => {
							if (
								google.maps.geometry.poly.containsLocation(
									point,
									new google.maps.Polygon({
										paths: polygon.geometry,
									}),
								)
							) {
								totalPoints += 1;
							}
						});
					}
				},
			);
			return totalPoints;
		},
		[],
	);

	const checkForOverlappingPolygons = useCallback(
		(polygon, polygonIndex): boolean => {
			let pointsInsidePolygonCount = 0;

			const allEditablePolygonVertices: google.maps.LatLng[] =
				getVerticesOfEditablePolygons(
					editablePolygonsList,
					polygonIndex,
				);
			// let allNeighbouringPolygonVertices: google.maps.LatLng[] =
			// 	getVerticesOfNeighbouringPolygons(neighbouringPolygonsList);
			pointsInsidePolygonCount +=
				getOverlappingPointsOfNeighbouringPolygonsCount(
					[
						...allEditablePolygonVertices,
						// ...allNeighbouringPolygonVertices,
					],
					polygon,
				) +
				getOverlappingPointsOfCurrentPolygonCount(
					editablePolygonsList,
					polygon,
					polygonIndex,
				);

			return pointsInsidePolygonCount > 0;
		},
		[
			editablePolygonsList,
			getOverlappingPointsOfCurrentPolygonCount,
			getOverlappingPointsOfNeighbouringPolygonsCount,
			getVerticesOfEditablePolygons,
		],
	);

	const checkForNonRejectedPoints = useCallback(
		(polygon: google.maps.Polygon): PolygonValidationStatus => {
			let validationStatus: PolygonValidationStatus = 'VALID';
			let pointsInsidePolygonCount = 0;
			let approvedPointsCount = 0;
			let firstPointPlaceId;
			let selectedPointEvent =
				pointsOfFarmId[selectedPoint!]?.selectedLayer?.PlaceSourceType;
			// eslint-disable-next-line no-restricted-syntax
			for (const point of Object.values(
				pointsOfFarmId,
			) as PointMetaData[]) {
				// check if point is inside the polygon and belongs to the same event type
				if (
					google.maps.geometry.poly.containsLocation(
						new google.maps.LatLng(point.geometry),
						polygon,
					)
				) {
					pointsInsidePolygonCount += 1;
					if (
						selectedPointEvent ===
						point?.selectedLayer?.PlaceSourceType
					) {
						// eslint-disable-next-line no-restricted-syntax
						for (const el of point.placeTags) {
							if (
								el.TagName === 'TagName_APPROVAL_STATUS' &&
								el.TagPropertyKey === 'STATUS' &&
								(el?.value?.stringValues[0] === 'APPROVED' ||
									el?.value?.stringValues[0] ===
										'IN_PROGRESS' ||
									el?.value?.stringValues[0] === 'REJECT')
							) {
								approvedPointsCount += 1;
								if (approvedPointsCount === 1) {
									firstPointPlaceId = Number(point.placeId);
								}
								break;
							}
						}
					}
				}
				if (approvedPointsCount > 1) break;
			}
			const selectedPointPlaceId = Number(selectedPoint?.split('-')[0]);
			if (approvedPointsCount > 1) {
				validationStatus = 'MULTIPLE_NON_REJECTED_POINTS_ENCLOSED';
			} else if (pointsInsidePolygonCount === 0) {
				validationStatus = 'NO_POINTS_ENCLOSED';
			} else if (firstPointPlaceId !== selectedPointPlaceId) {
				validationStatus = 'INCORRECT_PARENT_POINT';
			}
			validationStatus !== 'VALID' &&
				trackInvalidGeofenceDrawn(
					selectedPointPlaceId,
					selectedPoint,
					farmId,
					getUgdnFromLocalStorage(),
					validationStatus,
				);
			return validationStatus;
		},
		[farmId, pointsOfFarmId, selectedPoint],
	);

	const validateSelectedPolygon = useCallback(
		(
			polygon: google.maps.Polygon,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			polygonIndex: number,
		): PolygonValidationStatus => {
			// maintain this order of validations
			if (checkForOverlappingPolygons(polygon, polygonIndex)) {
				return 'OVERLAPPING_POLYGON';
			}

			if (
				editablePolygons[polygonIndex]?.parentPointSignatures
					?.length === 0
			) {
				return 'VALID';
			}
			return checkForNonRejectedPoints(polygon);
		},
		[
			checkForNonRejectedPoints,
			checkForOverlappingPolygons,
			editablePolygons,
		],
	);

	const getPolygonBorderColor = (polygonApprovalStatus: GeofenceStatus) => {
		return polygonApprovalStatus === 'GEOFENCING_SAVED'
			? '#65AA2D'
			: polygonApprovalStatus === 'GEOFENCING_COPIED'
			? '#65AA2D'
			: polygonApprovalStatus === 'GEOFENCING_REJECTED'
			? '#C31515'
			: '#EED60B';
	};

	return (
		<>
			{isDrawEnabled ? (
				<DrawingMode
					validateSelectedPolygon={validateSelectedPolygon}
				/>
			) : null}
			{/* newly drawn polygons and their markers */}
			{editablePolygonsList?.length > 0 &&
				editablePolygonsList.map((polygon: any, index: number) => (
					<PolygonWithMarkers
						borderStoke={getPolygonBorderColor(
							polygon.polygonApprovalStatus,
						)}
						colorIndex={index}
						fillColor="rgba(102, 246, 255, 0.1)"
						isInteractive
						key={`custom-polygon-${index}`}
						polygonIndex={Number(
							Object.keys(editablePolygons)[index],
						)}
						paths={polygon.geometry}
						polygonType="PRIMARY"
						validateSelectedPolygon={validateSelectedPolygon}
					/>
				))}
			{/* {Object.keys(editablePolygons)?.length > 0 && (
				<>
					<button
						className={classes.cancelEdit}
						onClick={() => {
							if (selectedPolygonIndex === undefined) return;
							restoreToPrevEdit && restoreToPrevEdit();
						}}
					>
						<ClearIcon />
					</button>
					<button
						className={classes.delete}
						onClick={() =>
							deleteSelectedPolygon && deleteSelectedPolygon()
						}
					>
						<DeleteForeverIcon />
					</button>
				</>
			)} */}
		</>
	);
};

export default React.memo(DrawingContainer);
